import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkle, DownloadSimple, FileXls, UploadSimple, ArrowClockwise, Quotes } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const downloadBlob = (data, filename, type) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([data], { type }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function AuditWorkbookPage() {
  const [books, setBooks] = useState(null);
  const [wb, setWb] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(0);
  const [openCite, setOpenCite] = useState(0);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadBooks = useCallback(() => {
    api.get("/audit-workbook").then((r) => setBooks(r.data?.items || [])).catch(() => setBooks([]));
  }, []);

  const loadBook = useCallback((wid) => {
    return api.get(`/audit-workbook/${wid}`).then((r) => {
      setWb(r.data);
      if (r.data?.analyzing && !pollRef.current) {
        pollRef.current = setInterval(() => {
          api.get(`/audit-workbook/${wid}`).then((rr) => {
            setWb(rr.data);
            if (!rr.data?.analyzing) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              toast.success("Analysis complete — review every answer before submitting");
            }
          }).catch(() => {});
        }, 4000);
      }
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { loadBooks(); }, [loadBooks]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/audit-workbook", fd, { timeout: 120000 });
      toast.success(`Parsed ${data.questions} questions (${data.rows} rows)`);
      loadBooks();
      loadBook(data.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const analyzeRow = async (row) => {
    setRowBusy(row);
    try {
      const { data } = await api.post(`/audit-workbook/${wb.id}/analyze/${row}`, {}, { timeout: 180000 });
      setWb((w) => ({ ...w, rows: w.rows.map((r) => (r.row === row ? { ...r, answer: data.answer, citations: data.citations } : r)) }));
      toast.success(`Row ${row} answered`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRowBusy(0);
    }
  };

  const analyzeAll = async () => {
    try {
      await api.post(`/audit-workbook/${wb.id}/analyze`);
      toast.success("Analyzing every unanswered question — this takes a few minutes; progress updates automatically");
      loadBook(wb.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveAnswer = async (row, answer) => {
    try {
      await api.put(`/audit-workbook/${wb.id}/row/${row}`, { answer });    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleConfirm = async (r) => {
    try {
      await api.put(`/audit-workbook/${wb.id}/row/${r.row}`, { confirmed: !r.confirmed });
      setWb((w) => ({ ...w, rows: w.rows.map((x) => (x.row === r.row ? { ...x, confirmed: !r.confirmed } : x)) }));
      toast.success(r.confirmed ? "Removed from knowledge base" : "Confirmed — saved to the audit knowledge base");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadWorkbook = async () => {
    try {
      const { data } = await api.get(`/audit-workbook/${wb.id}/download`, { responseType: "blob" });
      downloadBlob(data, `COMPLETED - ${wb.filename}`, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadPolicy = async (pid, title) => {
    try {
      const { data, headers } = await api.get(`/policies/${pid}/download`, { responseType: "blob" });
      const fname = (headers["content-disposition"] || "").match(/filename="?([^";]+)"?/)?.[1] || `${title}.pdf`;
      downloadBlob(data, fname, headers["content-type"]);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const answered = (wb?.rows || []).filter((r) => !r.section && (r.answer || "").trim()).length;
  const total = (wb?.rows || []).filter((r) => !r.section).length;

  return (
    <div className="max-w-5xl space-y-6" data-testid="audit-workbook-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Audit Questionnaire Workbook</h1>
        <p className="mt-1 max-w-3xl text-sm text-stone-500">
          Upload the payer/LTSS questionnaire (Excel). Analyze any question with one click — the answer is drafted in formal
          policy language, cites the exact passage from your policy library, and links straight to the policy document.
          Edit anything, then download the completed workbook. <strong>Review every draft before submitting.</strong>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <input ref={fileRef} type="file" accept=".xlsx" onChange={upload} className="hidden" data-testid="audit-wb-file" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="audit-wb-upload">
          <UploadSimple size={15} className="mr-1.5" /> {busy ? "Uploading…" : "Upload questionnaire (.xlsx)"}
        </Button>
        {(books || []).length > 0 && (
          <select value={wb?.id || ""} onChange={(e) => loadBook(e.target.value)}
            className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="audit-wb-select">
            <option value="">Open a previous upload…</option>
            {(books || []).map((b) => <option key={b.id} value={b.id}>{b.filename} · {String(b.created_at || "").slice(0, 10)}</option>)}
          </select>
        )}
        {wb && (
          <>
            <span className="text-xs font-medium text-stone-500" data-testid="audit-wb-progress">
              {wb.analyzing ? "Analyzing… " : ""}{answered}/{total} answered
            </span>
            <Button size="sm" variant="outline" onClick={analyzeAll} disabled={!!wb.analyzing} className="border-stone-300 text-xs" data-testid="audit-wb-analyze-all">
              <Sparkle size={13} className="mr-1" /> {wb.analyzing ? "Analyzing…" : "Analyze all unanswered"}
            </Button>
            <Button size="sm" variant="outline" onClick={downloadWorkbook} disabled={!answered} className="border-stone-300 text-xs" data-testid="audit-wb-download">
              <DownloadSimple size={13} className="mr-1" /> Download completed workbook
            </Button>
          </>
        )}
      </div>

      {wb && (
        <div className="space-y-3" data-testid="audit-wb-rows">
          {wb.rows.map((r) => r.section ? (
            <p key={r.row} className="pt-4 text-xs font-bold uppercase tracking-[0.15em] text-[#82778A]" data-testid={`audit-wb-section-${r.row}`}>{r.question}</p>
          ) : (
            <div key={r.row} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid={`audit-wb-row-${r.row}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 text-sm font-medium text-stone-900"><span className="mr-1.5 text-stone-300">#{r.row}</span>{r.question}
                  {r.from_approved && (
                    <span className="ml-2 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-bold text-[#1e8449]" data-testid={`audit-wb-approved-${r.row}`}>FROM APPROVED ANSWER</span>
                  )}
                  {r.confirmed && (
                    <span className="ml-2 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary" data-testid={`audit-wb-inkb-${r.row}`}>IN KNOWLEDGE BASE</span>
                  )}
                </p>
                <div className="flex shrink-0 gap-1.5">
                  {(r.answer || "").trim() && (
                    <Button size="sm" variant="outline" onClick={() => toggleConfirm(r)}
                      className={`border-stone-300 text-xs ${r.confirmed ? "text-primary" : ""}`} data-testid={`audit-wb-confirm-${r.row}`}>
                      {r.confirmed ? "Unconfirm" : "Confirm"}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => analyzeRow(r.row)} disabled={rowBusy === r.row || !!wb.analyzing}
                    className="border-stone-300 text-xs" data-testid={`audit-wb-analyze-${r.row}`}>
                    <ArrowClockwise size={12} className="mr-1" /> {rowBusy === r.row ? "Analyzing…" : (r.answer ? "Re-analyze" : "Analyze")}
                  </Button>
                </div>
              </div>
              {(r.answer || "").trim() ? (
                <>
                  <Textarea rows={4} defaultValue={r.answer} onBlur={(e) => saveAnswer(r.row, e.target.value)}
                    className="mt-3 border-stone-200 bg-accent/30 text-sm leading-relaxed" data-testid={`audit-wb-answer-${r.row}`} />
                  {r.gap && (
                    <p className="mt-2 rounded-md border border-[#B7791F]/30 bg-[#B7791F]/5 px-3 py-1.5 text-xs font-medium text-[#8a5d14]" data-testid={`audit-wb-gap-${r.row}`}>
                      NO POLICY COVERS THIS — {r.gap_note || "add one under Knowledge, then re-analyze"}. Left blank on purpose rather than boilerplate.
                    </p>
                  )}
                  {(r.policies_to_print || []).length > 0 && (
                    <p className="mt-2 text-xs text-stone-500" data-testid={`audit-wb-print-${r.row}`}>
                      Print for the surveyor: {(r.policies_to_print || []).map((p, i) => (
                        <span key={i} className="mr-2"><strong>{p.title}</strong>{p.underline ? <em> — underline: “{p.underline}”</em> : ""}</span>
                      ))}
                    </p>
                  )}
                  {(r.citations || []).length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {r.citations.map((c, ci) => (
                        <div key={ci} className="rounded-lg border border-stone-200 bg-white px-3 py-2" data-testid={`audit-wb-cite-${r.row}-${ci}`}>
                          <div className="flex items-center justify-between gap-2">
                            <button onClick={() => setOpenCite(openCite === r.row * 100 + ci ? 0 : r.row * 100 + ci)}
                              className="flex min-w-0 items-center gap-1.5 text-left text-xs font-semibold text-primary hover:underline"
                              data-testid={`audit-wb-cite-toggle-${r.row}-${ci}`}>
                              <Quotes size={13} className="shrink-0" /> {c.policy_title} — exact passage
                            </button>
                            <button onClick={() => downloadPolicy(c.policy_id, c.policy_title)}
                              className="shrink-0 text-xs font-semibold text-stone-500 hover:text-primary hover:underline"
                              data-testid={`audit-wb-cite-download-${r.row}-${ci}`}>
                              <DownloadSimple size={12} className="mr-0.5 inline" /> Download policy
                            </button>
                          </div>
                          {openCite === r.row * 100 + ci && (
                            <p className="mt-1.5 border-l-2 border-primary/40 pl-3 text-xs italic leading-relaxed text-stone-600" data-testid={`audit-wb-cite-text-${r.row}-${ci}`}>"{c.passage}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="mt-2 text-xs text-stone-400">Not answered yet — click Analyze.</p>
              )}
            </div>
          ))}
        </div>
      )}

      {!wb && (books || []).length === 0 && (
        <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-10 text-center" data-testid="audit-wb-empty">
          <FileXls size={32} className="mx-auto text-stone-300" />
          <p className="mt-2 text-sm text-stone-500">Upload the questionnaire Excel file to get started.</p>
        </div>
      )}
    </div>
  );
}
